// components/workflow/Booking.tsx
import React, { useState, FormEvent, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  GridLegacy as Grid,
  Paper,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  Stack
} from '@mui/material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import BookingConfirmation from './BookingConfirmation';
import {
  BookingDetails,
  validateDatePeriod,
  calculateDaysBetween
} from '../utils/emailService';
import { useTranslation } from 'react-i18next';

const Booking: React.FC = () => {
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [bookingData, setBookingData] = useState<BookingDetails | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isMultiDay, setIsMultiDay] = useState<boolean>(false);
  const [calculatedDays, setCalculatedDays] = useState<number>(1);

  const { t } = useTranslation();
  const fBookT = (key: string) => t(`workflowbooking:${key}`);

  // Form state with period support
  const [formState, setFormState] = useState<{
    serviceType: string;
    fromDate: string;
    toDate: string;
    time: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    address: string;
    notes: string;
    periodType?: 'single-day' | 'multi-day';
  }>({
    serviceType: '',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: '',
    time: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    address: '',
    notes: '',
    periodType: 'single-day'
  });

  const serviceTypes = [
    fBookT("serviceOption1"),
    fBookT("serviceOption2"),
    fBookT("serviceOption3"),
    fBookT("serviceOption4")
  ];

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00',
    '12:00', '13:00', '14:00', '15:00',
    '16:00', '17:00', '18:00', '19:00',
    'All Day (8:00-20:00)',
    'Morning (8:00-12:00)',
    'Afternoon (12:00-16:00)',
    'Evening (16:00-20:00)'
  ];

  // Auto-fill toDate when fromDate changes and multi-day is enabled
  useEffect(() => {
    if (isMultiDay && formState.fromDate && !formState.toDate) {
      // Default to same day for multi-day (user can change)
      setFormState(prev => ({
        ...prev,
        toDate: prev.fromDate
      }));
    } else if (!isMultiDay && formState.fromDate) {
      // For single day, set toDate = fromDate
      setFormState(prev => ({
        ...prev,
        toDate: prev.fromDate
      }));
    }
  }, [formState.fromDate, formState.toDate, isMultiDay]);

  // Calculate days when fromDate or toDate changes
  useEffect(() => {
    if (formState.fromDate && formState.toDate) {
      const days = calculateDaysBetween(formState.fromDate, formState.toDate);
      setCalculatedDays(days);
    }
  }, [formState.fromDate, formState.toDate]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Basic validations
    if (!formState.serviceType) errors.serviceType = fBookT("serviceTypeIsRequired");
    if (!formState.fromDate) errors.fromDate = fBookT("startDateIsRequired");
    if (!formState.toDate) errors.toDate = fBookT("endDateIsRequired");
    if (!formState.time) errors.time = fBookT("timeSlotIsRequired");
    if (!formState.customerName) errors.customerName = fBookT("nameIsRequired");
    if (!formState.customerEmail) {
      errors.customerEmail = fBookT("emailIsRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.customerEmail)) {
      errors.customerEmail = `${fBookT("invalidEmailFormat")}`;
    }

    if (!formState.customerPhone) errors.customerPhone = `${fBookT("phoneIsRequired")}`;

    // Date period validation
    if (formState.fromDate && formState.toDate) {
      if (!validateDatePeriod(formState.fromDate, formState.toDate)) {
        errors.toDate = `${fBookT("endDateCannotBeBeforeStartDate")}`;
      }

      // Optional: Limit maximum booking period (e.g., 30 days)
      const maxDays = 30;
      const days = calculateDaysBetween(formState.fromDate, formState.toDate);
      if (days > maxDays) {
        errors.toDate = `${fBookT("maximumBookingPeriod")} ${maxDays} ${fBookT("days")}`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const bookingDetails: BookingDetails = {
      serviceType: formState.serviceType,
      fromDate: formState.fromDate,
      toDate: formState.toDate,
      time: formState.time,
      duration: `${calculatedDays} ${fBookT("day")}${calculatedDays > 1 ? 's' : ''}`,
      customerName: formState.customerName,
      customerEmail: formState.customerEmail,
      customerPhone: formState.customerPhone,
      address: formState.address || 'Copenhagen, Denmark',
      notes: formState.notes,
      periodType: isMultiDay && formState.fromDate !== formState.toDate ? 'multi-day' : 'single-day'
    };

    setBookingData(bookingDetails);
    setShowConfirmation(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDateChange = (name: 'fromDate' | 'toDate', value: string): void => {
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear date errors
    if (formErrors[name] || formErrors.toDate) {
      setFormErrors(prev => ({
        ...prev,
        [name]: '',
        toDate: ''
      }));
    }
  };

  const handleMultiDayToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    setIsMultiDay(checked);

    if (!checked && formState.fromDate) {
      // Reset toDate to fromDate when switching to single day
      setFormState(prev => ({
        ...prev,
        toDate: prev.fromDate
      }));
    }
  };

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleBookingSuccess = (bookingId: string): void => {
    console.log(`Booking ${bookingId} successfully submitted`);

    // Reset form
    setFormState({
      serviceType: '',
      fromDate: '',
      toDate: '',
      time: '',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      address: '',
      notes: ''
    });
    setIsMultiDay(false);
    setCalculatedDays(1);

    // Close confirmation dialog after delay
    setTimeout(() => {
      setShowConfirmation(false);
    }, 2000);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          {fBookT("bookServices")}
        </Typography>

        <Grid container spacing={4}>
          {/* Booking Form */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Service Type */}
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!formErrors.serviceType}>
                      <InputLabel>{fBookT("formTitle")} *</InputLabel>
                      <Select
                        name="serviceType"
                        value={formState.serviceType}
                        onChange={(e) => setFormState(prev => ({ ...prev, serviceType: e.target.value }))}
                        label={fBookT("formTitle")}
                      >
                        {serviceTypes.map((service) => (
                          <MenuItem key={service} value={service}>
                            {service}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.serviceType && (
                        <Typography variant="caption" color="error">
                          {formErrors.serviceType}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Multi-Day Toggle */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isMultiDay}
                          onChange={handleMultiDayToggle}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{fBookT("multiDayBooking")}</Typography>
                          {isMultiDay && (
                            <Chip
                              label={`${calculatedDays} day${calculatedDays > 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </Grid>

                  {/* Date Selection */}
                  <Grid item xs={12} sm={isMultiDay ? 6 : 12}>
                    <TextField
                      fullWidth
                      type="date"
                      name="fromDate"
                      label={fBookT("startDate") + " *"}
                      value={formState.fromDate}
                      onChange={(e) => handleDateChange('fromDate', e.target.value)}
                      error={!!formErrors.fromDate}
                      helperText={formErrors.fromDate}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: getTodayDate(),
                        max: undefined
                      }}
                    />
                  </Grid>

                  {/* End Date (only for multi-day) */}
                  {isMultiDay && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        name="toDate"
                        label={fBookT("endDate") + " *"}
                        value={formState.toDate}
                        onChange={(e) => handleDateChange('toDate', e.target.value)}
                        error={!!formErrors.toDate}
                        helperText={formErrors.toDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: formState.fromDate || getTodayDate()
                        }}
                        disabled={!formState.fromDate}
                      />
                    </Grid>
                  )}

                  {/* Time Slot */}
                  <Grid item xs={12} sm={isMultiDay ? 6 : 12}>
                    <FormControl fullWidth error={!!formErrors.time}>
                      <InputLabel>{fBookT("formTime")} *</InputLabel>
                      <Select
                        name="time"
                        value={formState.time}
                        onChange={(e) => setFormState(prev => ({ ...prev, time: e.target.value }))}
                        label={fBookT("preferredTime") + " *"}
                      >
                        <MenuItem value="">
                          <em>{fBookT("selectTime")}</em>
                        </MenuItem>
                        {timeSlots.map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.time && (
                        <Typography variant="caption" color="error">
                          {formErrors.time}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Period Summary */}
                  {formState.fromDate && formState.toDate && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={false} sx={{ py: 1 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2">
                            <strong>{fBookT("period")}:</strong> {formatDateForDisplay(formState.fromDate)}
                            {formState.fromDate !== formState.toDate && (
                              <> to {formatDateForDisplay(formState.toDate)}</>
                            )}
                          </Typography>
                          <Typography variant="body2">
                            <strong>{fBookT("days")}:</strong> {calculatedDays} {fBookT("day")}{calculatedDays > 1 ? 's' : ''}
                          </Typography>
                        </Stack>
                      </Alert>
                    </Grid>
                  )}

                  {/* Customer Details */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="customerName"
                      label={`${fBookT("formName")} *`}
                      value={formState.customerName}
                      onChange={handleInputChange}
                      error={!!formErrors.customerName}
                      helperText={formErrors.customerName}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="email"
                      name="customerEmail"
                      label={fBookT("formEmail") + " *"}
                      value={formState.customerEmail}
                      onChange={handleInputChange}
                      error={!!formErrors.customerEmail}
                      helperText={formErrors.customerEmail}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="customerPhone"
                      label={fBookT("formPhone") + " *"}
                      value={formState.customerPhone}
                      onChange={handleInputChange}
                      error={!!formErrors.customerPhone}
                      helperText={formErrors.customerPhone}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="address"
                      label={fBookT("formAddress")}
                      value={formState.address}
                      onChange={handleInputChange}
                      placeholder="Street address, city, postcode"
                    />
                  </Grid>

                  {/* Additional Notes */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="notes"
                      label={fBookT("formNotes")}
                      value={formState.notes}
                      onChange={handleInputChange}
                      multiline
                      rows={4}
                      placeholder="Any special requirements, instructions, or details about your service needs..."
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        py: 2,
                        fontWeight: 600,
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      {isMultiDay ? fBookT("multiDayBooking") : fBookT("bookButton")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* Side Info Panel */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {fBookT("howItWorksTitle")}
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  1. {fBookT("howItWorksStep1")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  2. {fBookT("howItWorksStep2")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  3. {fBookT("howItWorksStep3")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  4. {fBookT("reviewBooking")}
                </Typography>
                <Typography component="li" variant="body2">
                  5. {fBookT("confirmationEmail")}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{fBookT("multiDayBooking")}:</strong> {` ${fBookT("multiDayBookingInfo")}`}
                </Typography>
              </Alert>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  {fBookT("serviceHours")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • {fBookT("monday")} - {fBookT("friday")}: 7:00 AM - 8:00 PM
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • {fBookT("saturday")}: 8:00 AM - 4:00 PM
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  • 24/7 {fBookT("emergency")}
                </Typography>
              </Box>

              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  {fBookT("needAssistance")}<br/> {fBookT("callUs")} (+45) 42 22 84 26
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Confirmation Dialog */}
        {showConfirmation && bookingData && (
          <BookingConfirmation
            bookingData={bookingData}
            onClose={() => setShowConfirmation(false)}
            onSuccess={handleBookingSuccess}
          />
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default Booking;